/**
 * Simple Segment tracking utilities
 * Provides easy-to-use functions for tracking events through Segment context
 */

import { useCallback, useContext } from "react";
import SegmentContext from "./SegmentContext";
import { TRACKING_EVENTS, SEGMENT_COMMON_PROPERTIES } from "./constants";
import { addDatadogError } from "src/utils/datadog";
import { getStoredOneTrustGroups } from "./oneTrustTracking";

/**
 * Hook to access Segment tracking functionality
 */
export const useSegmentTracking = () => {
  const { track, page } = useContext(SegmentContext);

  /**
   * Track Page View - fires on all pages. Sends track("Page View") only.
   * Memoized so callers can safely list it in useEffect dependency arrays.
   */
  const trackPageView = useCallback(() => {
    try {
      if (!track || typeof window === "undefined") return;

      const hostname = window.location.hostname;
      const pathname = window.location.pathname;
      if (!hostname || !pathname) return;

      const pageTitle = typeof document !== "undefined" ? document.title : "";
      const referrer = typeof document !== "undefined" ? document.referrer : "";
      const onetrustGroups = getStoredOneTrustGroups();
      const properties = {
        ...SEGMENT_COMMON_PROPERTIES,
        page_url: window.location.href,
        page_path: pathname,
        page_hostname: hostname,
        page_title: pageTitle,
        referrer,
        ...(onetrustGroups !== null && { onetrust_groups: onetrustGroups }),
      };
      track({
        eventName: TRACKING_EVENTS.PAGE_VIEW,
        properties,
      });
    } catch (error) {
      addDatadogError("SegmentPageViewTrackingError", String(error));
    }
  }, [track]);

  /**
   * Send Segment page() for Pardot form pages only. Call when a Pardot form is on the page
   * so destinations like Pardot receive a web visit for that form page.
   * Errors from analytics.page() are reported by SegmentContext (SegmentContextPageError); no local catch.
   * Memoized so callers (e.g. ComposableForm) can safely depend on it in useEffect without re-running every render.
   * All fields (url, path, title, referrer, search) are captured at invocation; when document.title is empty, pathname is used as the page name.
   */
  const trackPardotFormPageView = useCallback(async () => {
    if (!page || typeof window === "undefined") return;

    const pathname = window.location.pathname;
    if (!pathname) return;

    const href = window.location.href;
    const search = window.location.search;
    const referrer = typeof document !== "undefined" ? document.referrer : "";
    const titleSnapshot = typeof document !== "undefined" ? document.title : "";

    const pageProperties: Record<string, string> = {
      url: href,
      path: pathname,
      title: titleSnapshot,
      referrer,
      search,
    };
    return page(titleSnapshot || pathname, pageProperties);
  }, [page]);

  /**
   * Track Form Submission - fires on all form submissions
   * Wrapped in try-catch to ensure tracking failures don't affect form functionality
   */
  const trackFormSubmission = (
    formType: string,
    formId: string,
    formData?: Record<string, unknown>,
  ) => {
    try {
      if (!track || typeof window === "undefined") return;

      const hostname = window.location.hostname;
      const pathname = window.location.pathname;

      if (!hostname || !pathname) return;

      // Extract email from formData if available
      const userEmail =
        typeof formData?.email === "string" ? formData.email : undefined;

      const properties = {
        ...SEGMENT_COMMON_PROPERTIES,
        form_type: formType,
        form_id: formId,
        page_url: window.location.href,
        page_path: pathname,
        page_hostname: hostname,
        page_title: typeof document !== "undefined" ? document.title : "",
        referrer: typeof document !== "undefined" ? document.referrer : "",
        // Add email as separate property if available
        ...(userEmail && { user_email: userEmail }),
        // Include form data if provided (convert to string for Segment compatibility)
        ...(formData && { form_data: JSON.stringify(formData) }),
      };

      track({
        eventName: TRACKING_EVENTS.FORM_SUBMISSION,
        properties,
      });
    } catch (error) {
      // Silently fail - tracking should never break form functionality
      addDatadogError("SegmentFormSubmissionTrackingError", String(error));
    }
  };

  /**
   * Track OneTrust Groups Updated - fires when OneTrust cookie consent groups are updated
   * Wrapped in try-catch to ensure tracking failures don't affect OneTrust functionality
   */
  const trackOneTrustGroupsUpdated = (activeGroups?: string) => {
    try {
      if (!track || typeof window === "undefined") return;

      const hostname = window.location.hostname;
      const pathname = window.location.pathname;

      if (!hostname || !pathname) return;

      const properties = {
        ...SEGMENT_COMMON_PROPERTIES,
        active_groups: activeGroups || "",
        page_url: window.location.href,
        page_path: pathname,
        page_hostname: hostname,
        page_title: typeof document !== "undefined" ? document.title : "",
        referrer: typeof document !== "undefined" ? document.referrer : "",
      };

      track({
        eventName: TRACKING_EVENTS.ONETRUST_GROUPS_UPDATED,
        properties,
      });
    } catch (error) {
      // Silently fail - tracking should never break OneTrust functionality
      addDatadogError(
        "SegmentOneTrustGroupsUpdatedTrackingError",
        String(error),
      );
    }
  };

  return {
    trackPageView,
    trackPardotFormPageView,
    trackFormSubmission,
    trackOneTrustGroupsUpdated,
    /** Exposed so callers can gate on page availability (e.g. before consent); avoids firing ref guard before page() is actually sendable. */
    page,
  };
};
